import { useMemo, useState } from 'react';
import { EMPTY_FILTERS } from '../components/courses/courseListConstants';

export { SORT_OPTIONS, RATING_FILTER_OPTIONS, EMPTY_FILTERS } from '../components/courses/courseListConstants';

export function useCourseListFilters(courses, { defaultSort = 'desc' } = {}) {
  const [sort, setSort] = useState(defaultSort);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [showSort, setShowSort] = useState(false);

  const applyFilters = () => {
    setFilters(draftFilters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setShowFilters(false);
  };

  const openFilters = () => {
    setDraftFilters(filters);
    setShowFilters(true);
    setShowSort(false);
  };

  const filterOptions = useMemo(() => {
    const classesMap = new Map();
    const subjectsMap = new Map();
    const instructorsMap = new Map();

    (courses || []).forEach((course) => {
      if (course?.categoryId && course?.classLabel) {
        classesMap.set(String(course.categoryId), course.classLabel);
      }
      if (course?.subjectId && course?.subject) {
        subjectsMap.set(String(course.subjectId), course.subject);
      }
      if (course?.instructorId && course?.instructorName) {
        instructorsMap.set(String(course.instructorId), course.instructorName);
      }
    });

    const classes = Array.from(classesMap.entries()).map(([id, label]) => ({ id, label }));
    const subjects = Array.from(subjectsMap.entries()).map(([id, label]) => ({ id, label }));
    const instructors = Array.from(instructorsMap.entries()).map(([id, label]) => ({ id, label }));

    classes.sort((a, b) => a.label.localeCompare(b.label));
    subjects.sort((a, b) => a.label.localeCompare(b.label));
    instructors.sort((a, b) => a.label.localeCompare(b.label));

    return { classes, subjects, instructors };
  }, [courses]);

  const filteredCourses = useMemo(() => {
    const search = (filters.search || '').trim().toLowerCase();
    const minRating = filters.rating ? Number(filters.rating) : 0;
    const classSet = new Set((filters.classes || []).map(String));
    const subjectSet = new Set((filters.subjects || []).map(String));
    const instructorSet = new Set((filters.instructors || []).map(String));

    const list = (courses || []).filter((course) => {
      if (!course) return false;
      if (minRating && Number(course.rating || 0) < minRating) return false;
      if (classSet.size && !classSet.has(String(course.categoryId))) return false;
      if (subjectSet.size && !subjectSet.has(String(course.subjectId))) return false;
      if (instructorSet.size && !instructorSet.has(String(course.instructorId))) return false;
      if (search) {
        const hay = `${course.title || ''} ${course.instructorName || ''}`.toLowerCase();
        if (!hay.includes(search)) return false;
      }
      return true;
    });

    list.sort((a, b) => Number(a.id || 0) - Number(b.id || 0));
    if (sort === 'desc') list.reverse();
    return list;
  }, [courses, filters, sort]);

  const toggleDraftList = (key, id, checked) => {
    setDraftFilters((prev) => {
      const list = new Set((prev[key] || []).map(String));
      if (checked) list.add(String(id));
      else list.delete(String(id));
      return { ...prev, [key]: Array.from(list) };
    });
  };

  return {
    sort,
    setSort,
    filters,
    draftFilters,
    setDraftFilters,
    showFilters,
    setShowFilters,
    showSort,
    setShowSort,
    filterOptions,
    filteredCourses,
    applyFilters,
    clearFilters,
    openFilters,
    toggleDraftList,
  };
}
